import * as is from 'utilities/is';
import { h, Fragment } from 'preact';
import { rand } from 'utilities/data';
import { alphabeticOrdinal } from 'utilities/list';
import { useState, useRef } from 'preact/hooks';
import { ANSWER_TYPES } from 'constants/annotations';
import CheckIcon from '../../../img/icons/CheckIcon';
import {
    accessible,
    accessibleLabel,
    inaccessible
} from 'utilities/accessibility';
import { ProportionalImage } from 'rendering/components/proportional_image';

export const AnswerFactory = {
    getAnswer({questionType, ...answer}) {
        switch (questionType) {
            case ANSWER_TYPES.MULTIPLE_CHOICE:
                return <MultipleChoiceAnswer {...answer} />;
            
            case ANSWER_TYPES.CHECKBOX:
                return <CheckboxAnswer {...answer} />;

            case ANSWER_TYPES.BOOLEAN:
                return <BooleanAnswer {...answer} />;

            case ANSWER_TYPES.LINEAR_SCALE:
                return <LinearScaleAnswer {...answer} />;

            case ANSWER_TYPES.OPEN_RESPONSE:
                return <OpenResponseAnswer {...answer} />;

            default:
                return <MultipleChoiceAnswer {...answer} />;
        }
    }
}

export const MultipleChoiceAnswer = ({
    questionId = '',
    isQuestionAnswered,
    style = {},
    answers = [],
    defaultValue,
    selectedAnswer,
    showingCorrect    
}) => {
    const hasThumbnails = answers.some(a => a.properties?.thumbnail);
    const hasLocallySubmittedResponses = showingCorrect; 
    const renderCorrectnessLabel = (answerOptionIsCorrect, isStudentSelectionWrong) => {
        if (answerOptionIsCorrect) {
            return (
                <div className="answerEvaluation correctAnswer">
                    <CheckIcon />
                    <span className="correct-answer-text">Correct answer</span>
                </div>
            )
        }

        if (isStudentSelectionWrong) {
            return (
                <div className="answerEvaluation incorrectAnswer">
                    <span className="incorrect-answer-text">Your answer</span>
                </div>
            )
        }
        return null; 
    }
    
    const getIsStudentSelectionWrong = (hasPreviouslySubmittedStudentResponse, studentResponseIsCorrect, answerOptionIsCorrect, value) => {
        const playerWasPassedWrongAnswer = hasPreviouslySubmittedStudentResponse 
            && is.defined(studentResponseIsCorrect) 
            && studentResponseIsCorrect === false;
        const locallyStoredAnswerIsIncorrect = !answerOptionIsCorrect && selectedAnswer === value;
        return playerWasPassedWrongAnswer || locallyStoredAnswerIsIncorrect;
    }

    const getClassesForAnswer = ({
        isAnswered, 
        isStudentSelectionWrong, 
        isLocallySelectedAnswer, 
        isSelectedAndSubmittedAnswer, 
        answerOptionIsCorrect, 
        isPresentModeActive
    }) => {
        if (isPresentModeActive) {
            return null
        }

        const isSelectedAnswer = (isSelectedAndSubmittedAnswer || isLocallySelectedAnswer);
        const isAnsweredButOptionNotSelected = isAnswered && !isSelectedAnswer;

        if (!isAnswered || (isStudentSelectionWrong && isAnsweredButOptionNotSelected)) return '';
        const shouldApplyCorrectClass = (isSelectedAnswer && !isStudentSelectionWrong) || answerOptionIsCorrect;
        const shouldApplyIncorrectClass = isSelectedAnswer && isStudentSelectionWrong;

        if (shouldApplyCorrectClass) {
            return 'isAnswered correctAnswerContainer';
        }
        
        if (shouldApplyIncorrectClass) {
            return 'isAnswered incorrectAnswerContainer';
        };
    }

    return (
        <ol data-has-thumbnails={hasThumbnails ? 'true' : 'false'}>
            {
                answers.map((a,i) => {
                    const { 
                        id, 
                        properties = {}, 
                        content,
                        isPresentModeActive, 
                        studentResponseIsCorrect, 
                        hasPreviouslySubmittedStudentResponse = false, 
                        studentSelectedAnswerId = '' 
                    } = a;

                    const unique = rand(1000000); // IDs must be unique otherwise `htmlFor` could interact with the wrong input element
                    const inputEl = useRef(null);
                    const onEnter = () => inputEl.current.click();
                    let { value, thumbnail, correct: answerOptionIsCorrect } = properties;
                    const isStudentSelectionWrong = getIsStudentSelectionWrong(hasPreviouslySubmittedStudentResponse, studentResponseIsCorrect, answerOptionIsCorrect, value);
                    const isLocallySelectedAnswer = selectedAnswer && parseInt(selectedAnswer?.slice(-1)) === i + 1;
                    const isAnswered = hasLocallySubmittedResponses || hasPreviouslySubmittedStudentResponse;
                    const isSelectedAndSubmittedAnswer = is.defined(studentResponseIsCorrect)
                    const correctnessLabel = renderCorrectnessLabel(answerOptionIsCorrect, isStudentSelectionWrong);

                    value = is.defined(value) ? value : content;

                    const isLocallySelectedAnswerOrStudentSelected = (isLocallySelectedAnswer || !!studentSelectedAnswerId)
                    const classNameForAnswerProps = {
                        isAnswered,
                        isStudentSelectionWrong,
                        isLocallySelectedAnswerOrStudentSelected,
                        isSelectedAndSubmittedAnswer,
                        answerOptionIsCorrect,
                        isPresentModeActive
                    }

                    return (
                        <li key={id} data-answered={isAnswered}>
                            <input
                                defaultChecked={defaultValue === value}
                                ref={inputEl}
                                id={`${id}-${unique}`}
                                type="radio"
                                name={questionId}
                                value={value}
                                disabled={isAnswered}
                                {...accessible(onEnter, 'radio')}
                            />
                            <label
                                data-labeling-answer={hasLocallySubmittedResponses}
                                style={style}
                                htmlFor={`${id}-${unique}`}
                                data-has-thumbnail={String(!!thumbnail)}
                            >
                                {thumbnail && <ProportionalImage className="thumbnail" src={thumbnail} content={content} ratio={100} />}
                                <div className={"answer " + getClassesForAnswer(classNameForAnswerProps)}>
                                    {(!isPresentModeActive && isAnswered) && correctnessLabel}
                                    {isLocallySelectedAnswer && !showingCorrect ? <div className="selectedStateIndicator" /> : null}
                                    <div className="ordinal">{alphabeticOrdinal(i)}</div>
                                    <div className="answer-content">{content}</div> 
                                </div>                                                               
                            </label>
                        </li>
                    )
                })
            }
        </ol>
    )
}

export const CheckboxAnswer = ({
    questionId = '',
    style = {},
    answers = [],
    defaultValue
}) => {
    const hasThumbnails = answers.some(a => a.properties?.thumbnail);

    return (
        <ol data-has-thumbnails={hasThumbnails ? 'true' : 'false'}>
            {
                answers.map(a => {
                    const { id, properties = {}, content } = a;
                    const unique = rand(1000000); // IDs must be unique otherwise `htmlFor` could interact with the wrong input element
                    const inputEl = useRef(null);
                    const onEnter = () => inputEl.current.click();
                    let { value, thumbnail } = properties;

                    value = is.defined(value) ? value : content;

                    return (
                        <li key={id}>
                            <input
                                defaultChecked={defaultValue === value}
                                ref={inputEl}
                                id={`${id}-${unique}`}
                                type="checkbox"
                                name={questionId}
                                value={value}
                                {...inaccessible()}
                            />
                            <label
                                style={style}
                                htmlFor={`${id}-${unique}`}
                                data-has-thumbnail={String(!!thumbnail)}
                                {...accessibleLabel(onEnter)}
                            >
                                {thumbnail && <ProportionalImage className="thumbnail" src={thumbnail} content={content} ratio={100} />}
                                {content}
                            </label>
                        </li>
                    )
                })
            }
        </ol>
    )
}

export const BooleanAnswer = ({
    id = '',
    questionId = '',
    answers = [],
    style = {},
    defaultValue
}) => {
    const unique = rand(1000000);
    const uniqueId = `${id}-${unique}`;
    const [answer, setAnswer] = useState(defaultValue);
    const chooseAnswer = value => e => setAnswer(value);
    const aValue = answers[0].properties?.value;

    return (
        <div className="booleanAnswer" role="switch" aria-checked={answer === aValue}>
            <input id={uniqueId}
                type="hidden"
                name={questionId}
                value={answer}
            />
            {
                answers.map(({ content, properties = {} }) => {
                    const { value, thumbnail } = properties;
                    const onClick = chooseAnswer(value);
                    const attrs = {
                        style,
                        onClick,
                        htmlFor: uniqueId,
                        'data-selected': answer === value,
                        'data-has-thumbnail': thumbnail ? 'true' : 'false',
                        ...accessibleLabel(onClick)
                    };

                    return (
                        <label {...attrs} >
                            {thumbnail && <ProportionalImage className="thumbnail" src={thumbnail} content={content} ratio={75} size="contain" />}
                            {content}
                        </label>
                    )
                })
            }
        </div>
    )
}

export const LinearScaleAnswer = ({
    id = '',
    answers = [],
    style = {},
    defaultValue
}) => {
    const unique = rand(1000000);
    const uniqueId = `${id}-${unique}`;
    const [low, high] = answers;
    const [answer, setAnswer] = useState(defaultValue);
    const min = low.properties?.value || 1;
    const max = high.properties?.value || 10;
    const onChange = e => {
        const { value } = e.target;
        setAnswer(value);
    };
    const defaultAnswer = Math.floor(min/2 + max/2);
    const rangeOptions = {
        id,
        min,
        max,
        name: id,
        onChange,
        type: 'range',
        value: answer,
        defaultAnswer,
        defaultValue,
        ['data-has-answer']: is.defined(answer)
    };
    const hasThumbnails = answers.some(a => a.properties?.thumbnail);
    style = { ...style, border: 'none' }; // if you don't copy object, it affects the style of other answer types

    return (
        <div className="linearScaleAnswer" style={style} data-has-thumbnails={hasThumbnails}>
            {
                answers.map(({ content, properties = {} }) => {
                    const { thumbnail, value } = properties;
                    const onClick = () => setAnswer(value);

                    return (
                        <label
                            htmlFor={uniqueId}
                            data-has-thumbnail={thumbnail ? 'true' : 'false'}
                            onClick={onClick}
                            {...accessibleLabel(onClick)}
                        >
                            {thumbnail && <img aria-label={content} className="thumbnail" src={thumbnail} />}
                            {content}
                        </label>
                    )
                })
            }
            <div className="range-display">
                <input {...rangeOptions} />
                <h3>{answer || defaultAnswer}</h3>
            </div>          
        </div>
    )
}

export const OpenResponseAnswer = ({
    id = '',
    questionId = '',
    answers = [],
    style = {},
    defaultValue
}) => {
    const [boundaries] = answers;
    const { content, properties = {} } = boundaries;
    const { min, max } = properties;
    const [charCount, setCharCount] = useState(0);
    const onChange = ({target}) => setCharCount(target.value.length);

    return (
        <div className="openResponseAnswer">
            <textarea
                defaultValue={defaultValue}
                style={style}
                onChange={onChange}
                id={id}
                name={questionId}
                placeholder={content}
                maxlength={max}
            />
            <div className="characterDetails" style={{color: style.color}}>
                <div>min: {min}</div>
                <div>{charCount} / max: {max}</div>
            </div>
        </div>
    )
}
